/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';

/**
 * Aligns with {@link ToastSeverity} in `@ecsper/speciality-contract` without coupling packages.
 */
export type ToastSeverity = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  message: string;
  severity: ToastSeverity;
  onDismiss: (id: string) => void;
}

interface ToastItem {
  id: string;
  message: string;
  severity: ToastSeverity;
}

interface ToastContextValue {
  toast: (message: string, severity: ToastSeverity) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastIcon({ severity }: { severity: ToastSeverity }) {
  const common = {
    width: '1.25em',
    height: '1.25em',
    display: 'block' as const,
  };

  switch (severity) {
    case 'info':
      return (
        <svg
          {...common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path strokeLinecap="round" d="M12 10v6M12 8h.01" />
        </svg>
      );
    case 'success':
      return (
        <svg
          {...common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'warning':
      return (
        <svg
          {...common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4l8 14H4L12 4z" />
          <path strokeLinecap="round" d="M12 10v4M12 17h.01" />
        </svg>
      );
    case 'error':
      return (
        <svg
          {...common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path strokeLinecap="round" d="M15 9l-6 6M9 9l6 6" />
        </svg>
      );
  }
}

/**
 * Single toast row: severity icon plus message (T2: not colour-only). No spinner animations (T6).
 */
export function Toast({ id, message, severity, onDismiss }: ToastProps) {
  const autoDismiss = severity === 'info' || severity === 'success';

  useEffect(() => {
    if (!autoDismiss) return;
    const timer = globalThis.setTimeout(() => {
      onDismiss(id);
    }, 5000);
    return () => globalThis.clearTimeout(timer);
  }, [autoDismiss, id, onDismiss]);

  const role = severity === 'warning' || severity === 'error' ? 'alert' : 'status';

  return (
    <div className={`ecs-toast ecs-toast--${severity}`} role={role}>
      <span className="ecs-toast__icon">
        <ToastIcon severity={severity} />
      </span>
      <div className="ecs-toast__body">{message}</div>
      <button
        type="button"
        className="ecs-toast__dismiss"
        aria-label="Dismiss notification"
        onClick={() => {
          onDismiss(id);
        }}
      >
        <svg
          width="1.125em"
          height="1.125em"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const regionId = useId();

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: string, severity: ToastSeverity) => {
    const id =
      globalThis.crypto !== undefined && 'randomUUID' in globalThis.crypto
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setItems((prev) => [...prev, { id, message, severity }]);
    return id;
  }, []);

  const value = useMemo(
    () => ({
      toast,
      dismiss,
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="ecs-toast-region" id={regionId} aria-label="Notifications">
        {items.map((item) => (
          <Toast
            key={item.id}
            id={item.id}
            message={item.message}
            severity={item.severity}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
